import {Route} from '@angular/router';
import {Type} from '@angular/core';
import {LayoutComponent} from 'app/layout/layout.component';
import {InitialDataResolver} from 'app/app.resolvers';
import {AuthPipe, firebaseAuthGuard, redirectLoggedInTo, redirectUnauthorizedTo} from './core/auth/guards/firebase-auth.guard';
import {NoAuthGuard} from './core/auth/guards/noAuth.guard';
import {RoleGuard} from './core/auth/guards/role.guard';
import {KpiEntryGuard} from './core/auth/guards/kpi-entry.guard';
import {ServerAccountRoleGuard} from './core/auth/guards/server-account-role.guard';
import {SirocEnabledGuard} from './core/auth/guards/siroc-enabled.guard';
import {AuthConfirmationRequiredModule} from '@auth/confirmation-required/confirmation-required.module';
import {AuthForgotPasswordModule} from '@auth/forgot-password/forgot-password.module';
import {AuthResetPasswordModule} from '@auth/reset-password/reset-password.module';
import {AuthSignInModule} from '@auth/sign-in/sign-in.module';
import {AuthSignUpModule} from '@auth/sign-up/sign-up.module';
import {AuthSignOutModule} from '@auth/sign-out/sign-out.module';
import {AuthUnlockSessionModule} from '@auth/unlock-session/unlock-session.module';
import {Error404Module} from '@master/pages/error/error-404/error-404.module';
import {HelpModule} from '@master/help/help.module';
import {SemiMasterUsersModule} from '@master/semi-master-users/semi-master-users.module';
import {KpisModule} from '@hotels/kpis/kpis.module';
import {ExecutiveDashboardModule} from '@master/dashboards/executive/executive-dashboard.module';
import {UserModule} from './layout/common/user/user.module';
import {DetailAdminModule} from '@master/detail-admin/detail-admin.module';
import {UpdateHotelModule} from '@master/hotels/update/update-hotel.module';
import {ReferEarnModule} from '@hotels/refer-earn/refer-earn.module';
import {UserHotelModule} from '@master/hotels/users/userHotel.module';
import {InOutModule} from '@hotels/in-out/in-out.module';
import {ProvidersModule} from '@master/hotels/providers/providers.module';
import {AddProviderModule} from '@master/hotels/providers/addProvider.module';
import {DetailHotelModule} from '@hotels/detail-hotel/detail-hotel.module';
import {ContactsModule} from '@hotels/providers/contacts.module';
import {ModernModule} from '@hotels/invoice/modern/modern.module';
import {IndicatorsModule} from '@hotels/indicators/indicators.module';
import {MassiveDownloadModule} from '@hotels/massive-download/massive-download.module';
import {NewPasswordModule} from '@master/new-password/new-password.module';
import {UpdateInitLoadModule} from '@provider/update-init-load/updateInitLoad.module';
import {ProviderHotelEvidencesModule} from '@provider/hotels/provider-hotel-evidences.module';
import {ECommerceModule} from '@provider/employees/ecommerce.module';
import {AccountRole} from '@enums/AccountRole';
import {SemiMasterAllowedHotelsGuard} from '@core/auth/guards/semi-master-allowed-hotels.guard';
import {MasterProviderHotelAccessGuard} from '@core/auth/guards/master-provider-hotel-access.guard';
import {ImssEmployerRegistryModule} from '@provider/imss-employer-registry/imss-employer-registry.module';
import {SirocProveedorModule} from '@provider/siroc/siroc-proveedor.module';
import {IcsoeModule} from "@master/icsoe/icsoe.module";
import {SirocModule} from "@hotels/siroc/siroc.module";
import {AdminReportsModule} from '@master/admin-reports/admin-reports.module';
import {FianzasMasterModule} from '@master/fianzas-master/fianzas-master.module';
import {FianzasSemiMasterModule} from '@master/fianzas-semi-master/fianzas-semi-master.module';
import {PolizasModule} from '@provider/polizas/polizas.module';
import {PolizasMasterModule} from '@master/polizas/polizas-master.module';
import {PolizasCompanyModule} from '@hotels/polizas/polizas-company.module';
import {FianzasCompanyModule} from '@hotels/fianzas/fianzas-company.module';
import {ContracteeEvidenceConfigModule} from '@hotels/contractee-evidence-config/contractee-evidence-config.module';

/**
 * Creator: Fernando Tapia
 * Company: TAXSAT
 * Date: 20.01.22
 *
 * Copyright TAXSAT
 */
const redirectUnauthorizedToLogin: (() => AuthPipe) = (): AuthPipe => redirectUnauthorizedTo(['/sign-in']);
const redirectLoggedInToItems: (() => AuthPipe) = (): AuthPipe => redirectLoggedInTo(['/master/kpis']);
export const appRoutes: Route[] = [

    // Redirect the empty path to '/example'
    {path: '', pathMatch: 'full', redirectTo: 'master/kpis'},

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect' path.
    // Below is another redirection for that path to redirect the user to the desired location.
    // This is a small convenience to keep all main routes together here in this file.
    {path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'master/kpis'},

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
            data: {authGuardPipe: redirectLoggedInToItems}
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: (): Promise<Type<AuthConfirmationRequiredModule>> =>
                    import('app/modules/auth/confirmation-required/confirmation-required.module').then(m => m.AuthConfirmationRequiredModule)
            },
            {
                path: 'forgot-password',
                loadChildren: (): Promise<Type<AuthForgotPasswordModule>> =>
                    import('app/modules/auth/forgot-password/forgot-password.module').then(m => m.AuthForgotPasswordModule)
            },
            {
                path: 'reset-password',
                loadChildren: (): Promise<Type<AuthResetPasswordModule>> =>
                    import('app/modules/auth/reset-password/reset-password.module').then(m => m.AuthResetPasswordModule)
            },
            {
                path: 'sign-in',
                loadChildren: (): Promise<Type<AuthSignInModule>> =>
                    import('app/modules/auth/sign-in/sign-in.module').then(m => m.AuthSignInModule)
            },
            {
                path: 'sign-up',
                loadChildren: (): Promise<Type<AuthSignUpModule>> =>
                    import('app/modules/auth/sign-up/sign-up.module').then(m => m.AuthSignUpModule)
            }
        ]
    },
    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [firebaseAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
            data: {authGuardPipe: redirectLoggedInToItems}
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: (): Promise<Type<AuthSignOutModule>> =>
                    import('app/modules/auth/sign-out/sign-out.module').then(m => m.AuthSignOutModule)
            },
            {
                path: 'unlock-session',
                loadChildren: (): Promise<Type<AuthUnlockSessionModule>> =>
                    import('app/modules/auth/unlock-session/unlock-session.module').then(m => m.AuthUnlockSessionModule)
            }
        ]
    },
    {
        path: '',
        canActivate: [firebaseAuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {authGuardPipe: redirectUnauthorizedToLogin},
        children: [
            {path: '', redirectTo: '/sign-in', pathMatch: 'full'},
            // 404 & Catch all
            {
                path: '404-not-found',
                pathMatch: 'full',
                loadChildren: (): Promise<Type<Error404Module>> =>
                    import('app/modules/master/pages/error/error-404/error-404.module').then(m => m.Error404Module)
            }, {
                path: 'help',
                pathMatch: 'full',
                loadChildren: (): Promise<Type<HelpModule>> =>
                    import('app/modules/master/help/help.module').then(m => m.HelpModule)
            },
            // Dashboards
            {
                path: 'master', children: [
                    {
                        path: 'semi-master-users',
                        canActivate: [RoleGuard],
                        data: {allowedRoles: [AccountRole.master]},
                        loadChildren: (): Promise<Type<SemiMasterUsersModule>> =>
                            import('app/modules/master/semi-master-users/semi-master-users.module').then(m => m.SemiMasterUsersModule)
                    },
                    {
                        path: 'kpis',
                        canActivate: [KpiEntryGuard],
                        loadChildren: (): Promise<Type<KpisModule>> =>
                            import('app/modules/master/dashboards/kpis/kpis.module').then(m => m.KpisModule)
                    }, {
                        path: 'executive-dashboard',
                        canActivate: [RoleGuard],
                        data: {allowedRoles: [AccountRole.master, AccountRole.semiMaster, AccountRole.adminHotel, AccountRole.accountant]},
                        loadChildren: (): Promise<Type<ExecutiveDashboardModule>> =>
                            import('app/modules/master/dashboards/executive/executive-dashboard.module').then(m => m.ExecutiveDashboardModule)
                    }, {
                        path: 'icsoe',
                        loadChildren: (): Promise<Type<IcsoeModule>> =>
                            import('app/modules/master/icsoe/icsoe.module').then(m => m.IcsoeModule)
                    }, {
                        path: 'admin-reports',
                        loadChildren: (): Promise<Type<AdminReportsModule>> =>
                            import('app/modules/master/admin-reports/admin-reports.module').then(m => m.AdminReportsModule)
                    }, {
                        path: 'fianzas',
                        canActivate: [RoleGuard],
                        data: {allowedRoles: [AccountRole.master]},
                        loadChildren: (): Promise<Type<FianzasMasterModule>> =>
                            import('app/modules/master/fianzas-master/fianzas-master.module').then(m => m.FianzasMasterModule)
                    }, {
                        path: 'fianzas-semi',
                        canActivate: [RoleGuard],
                        data: {allowedRoles: [AccountRole.semiMaster]},
                        loadChildren: (): Promise<Type<FianzasSemiMasterModule>> =>
                            import('app/modules/master/fianzas-semi-master/fianzas-semi-master.module').then(m => m.FianzasSemiMasterModule)
                    }, {
                        path: 'users',
                        loadChildren: (): Promise<Type<UserModule>> =>
                            import('app/modules/master/dashboards/users/user.module').then(m => m.UserModule)
                    }, {
                        path: 'profile',
                        loadChildren: (): Promise<Type<DetailAdminModule>> =>
                            import('app/modules/master/detail-admin/detail-admin.module').then(m => m.DetailAdminModule)
                    }, {
                        canActivate: [RoleGuard, SemiMasterAllowedHotelsGuard],
                        data: {allowedRoles: [AccountRole.master, AccountRole.semiMaster]},
                        path: 'update-hotel/:hotelId',
                        loadChildren: (): Promise<Type<UpdateHotelModule>> =>
                            import('app/modules/master/hotels/update/update-hotel.module').then(m => m.UpdateHotelModule)
                    }, {
                        path: 'refer',
                        canActivate: [RoleGuard],
                        data: {allowedRoles: [AccountRole.master]},
                        loadChildren: (): Promise<Type<ReferEarnModule>> =>
                            import('app/modules/hotels/refer-earn/refer-earn.module').then(m => m.ReferEarnModule)
                    }, {
                        path: 'polizas',
                        canActivate: [RoleGuard],
                        data: {allowedRoles: [AccountRole.master, AccountRole.semiMaster]},
                        loadChildren: (): Promise<Type<PolizasMasterModule>> =>
                            import('app/modules/master/polizas/polizas-master.module').then(m => m.PolizasMasterModule)
                    }
                ]
            },
            // Dashboards
            {
                path: 'hotels', children: [
                    {
                        path: 'contractee-evidence-config/:contracteeId',
                        canActivate: [RoleGuard, SemiMasterAllowedHotelsGuard],
                        data: {allowedRoles: [AccountRole.master, AccountRole.semiMaster]},
                        loadChildren: (): Promise<Type<ContracteeEvidenceConfigModule>> =>
                            import('app/modules/hotels/contractee-evidence-config/contractee-evidence-config.module').then(m => m.ContracteeEvidenceConfigModule)
                    }, {
                        path: 'contractee-evidence-config',
                        loadChildren: (): Promise<Type<ContracteeEvidenceConfigModule>> =>
                            import('app/modules/hotels/contractee-evidence-config/contractee-evidence-config.module').then(m => m.ContracteeEvidenceConfigModule)
                    }, {
                        path: 'users',
                        loadChildren: (): Promise<Type<UserHotelModule>> =>
                            import('app/modules/master/hotels/users/userHotel.module').then(m => m.UserHotelModule)
                    }, {
                        path: 'inout',
                        loadChildren: (): Promise<Type<InOutModule>> =>
                            import('app/modules/hotels/in-out/in-out.module').then(m => m.InOutModule)
                    }, {
                        path: 'kpis',
                        loadChildren: (): Promise<Type<KpisModule>> =>
                            import('app/modules/hotels/kpis/kpis.module').then(m => m.KpisModule)
                    }, {
                        path: 'provider',
                        loadChildren: (): Promise<Type<ProvidersModule>> =>
                            import('app/modules/master/hotels/providers/providers.module').then(m => m.ProvidersModule)
                    }, {
                        path: 'add-provider',
                        loadChildren: (): Promise<Type<AddProviderModule>> =>
                            import('app/modules/master/hotels/providers/addProvider.module').then(m => m.AddProviderModule)
                    }, {
                        path: 'profile',
                        loadChildren: (): Promise<Type<DetailHotelModule>> =>
                            import('app/modules/hotels/detail-hotel/detail-hotel.module').then(m => m.DetailHotelModule)
                    }, {
                        path: 'providers',
                        canActivate: [ServerAccountRoleGuard],
                        data: {allowedRoles: [AccountRole.adminHotel, AccountRole.accountant]},
                        loadChildren: (): Promise<Type<ContactsModule>> =>
                            import('app/modules/hotels/providers/contacts.module').then(m => m.ContactsModule)
                    }, {
                        canActivate: [RoleGuard, SemiMasterAllowedHotelsGuard, MasterProviderHotelAccessGuard],
                        runGuardsAndResolvers: 'paramsChange',
                        data: {allowedRoles: [AccountRole.master, AccountRole.semiMaster]},
                        path: 'providers-master/:hotelId',
                        loadChildren: (): Promise<Type<ContactsModule>> =>
                            import('app/modules/hotels/providers/contacts.module').then(m => m.ContactsModule)
                    }, {
                        path: 'invoice/:id',
                        loadChildren: (): Promise<Type<ModernModule>> =>
                            import('app/modules/hotels/invoice/modern/modern.module').then(m => m.ModernModule)
                    }, {
                        path: 'indicators',
                        loadChildren: (): Promise<Type<IndicatorsModule>> =>
                            import('app/modules/hotels/indicators/indicators.module').then(m => m.IndicatorsModule)
                    }, {
                        path: 'downloads',
                        loadChildren: (): Promise<Type<MassiveDownloadModule>> =>
                            import('app/modules/hotels/massive-download/massive-download.module').then(m => m.MassiveDownloadModule)
                    }, {
                        path: 'refer',
                        loadChildren: (): Promise<Type<ReferEarnModule>> =>
                            import('app/modules/hotels/refer-earn/refer-earn.module').then(m => m.ReferEarnModule)
                    }, {
                        path: 'siroc',
                        canActivate: [SirocEnabledGuard],
                        loadChildren: (): Promise<Type<SirocModule>> =>
                            import('app/modules/hotels/siroc/siroc.module').then(m => m.SirocModule)
                    }, {
                        path: 'polizas',
                        canActivate: [RoleGuard],
                        data: {allowedRoles: [AccountRole.adminHotel, AccountRole.accountant]},
                        loadChildren: (): Promise<Type<PolizasCompanyModule>> =>
                            import('app/modules/hotels/polizas/polizas-company.module').then(m => m.PolizasCompanyModule)
                    }, {
                        path: 'fianzas',
                        canActivate: [RoleGuard],
                        data: {allowedRoles: [AccountRole.adminHotel, AccountRole.accountant]},
                        loadChildren: (): Promise<Type<FianzasCompanyModule>> =>
                            import('app/modules/hotels/fianzas/fianzas-company.module').then(m => m.FianzasCompanyModule)
                    }
                ]
            }, {
                path: 'providers', children: [
                    {
                        path: 'new-password',
                        loadChildren: (): Promise<Type<NewPasswordModule>> =>
                            import('app/modules/master/new-password/new-password.module').then(m => m.NewPasswordModule)
                    }, {
                        path: 'init-load',
                        loadChildren: (): Promise<Type<AddProviderModule>> =>
                            import('app/modules/master/hotels/providers/addProvider.module').then(m => m.AddProviderModule)
                    }, {
                        path: 'update-init-load',
                        loadChildren: (): Promise<Type<UpdateInitLoadModule>> =>
                            import('app/modules/provider/update-init-load/updateInitLoad.module').then(m => m.UpdateInitLoadModule)
                    }, {
                        path: 'hotels',
                        canActivate: [],
                        loadChildren: (): Promise<Type<ProviderHotelEvidencesModule>> =>
                            import('app/modules/provider/hotels/provider-hotel-evidences.module').then(m => m.ProviderHotelEvidencesModule)
                    }, {
                        path: 'employees',
                        loadChildren: (): Promise<Type<ECommerceModule>> =>
                            import('app/modules/provider/employees/ecommerce.module').then(m => m.ECommerceModule)
                    }, {
                        path: 'imss-employer-registry',
                        canActivate: [RoleGuard],
                        data: {allowedRoles: [AccountRole.provider]},
                        loadChildren: (): Promise<Type<ImssEmployerRegistryModule>> =>
                            import('@provider/imss-employer-registry/imss-employer-registry.module').then(m => m.ImssEmployerRegistryModule)
                    }, {
                        path: 'refer',
                        loadChildren: (): Promise<Type<ReferEarnModule>> =>
                            import('app/modules/hotels/refer-earn/refer-earn.module').then(m => m.ReferEarnModule)
                    }, {
                        path: 'polizas',
                        canActivate: [RoleGuard],
                        data: {allowedRoles: [AccountRole.provider]},
                        loadChildren: (): Promise<Type<PolizasModule>> =>
                            import('app/modules/provider/polizas/polizas.module').then(m => m.PolizasModule)
                    }, {
                        path: 'siroc',
                        canActivate: [RoleGuard, SirocEnabledGuard],
                        data: {allowedRoles: [AccountRole.provider]},
                        loadChildren: (): Promise<Type<SirocProveedorModule>> =>
                            import('@provider/siroc/siroc-proveedor.module').then(m => m.SirocProveedorModule)
                    }
                ]
            },
            {path: '**', redirectTo: '404-not-found'}
        ]
    }
];
